import { useEffect, useRef, useState } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

import {
  MAX_COMMAND_DESCRIPTION_LENGTH,
  MAX_COMMAND_NAME_LENGTH,
  MAX_COMMAND_PROMPT_LENGTH,
} from './constants';
import {
  validateCommandName,
  validateDescription,
  validatePromptText,
} from './slashCommandUtils';

interface SlashCommandFormFieldsProps {
  initialName?: string;
  initialDescription?: string;
  initialPromptText?: string;
  existingNames?: string[];
  onSubmit: (data: {
    name: string;
    description: string;
    promptText: string;
  }) => void;
  onCancel: () => void;
  submitLabel?: string;
}

export const SlashCommandFormFields = ({
  initialName = '',
  initialDescription = '',
  initialPromptText = '',
  existingNames = [],
  onSubmit,
  onCancel,
  submitLabel = 'Save Command',
}: SlashCommandFormFieldsProps) => {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [promptText, setPromptText] = useState(initialPromptText);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    const nameError = validateCommandName(name);
    if (nameError) {
      newErrors.name = nameError;
    } else if (
      existingNames.some(
        (n) =>
          n.toLowerCase() === name.toLowerCase() &&
          n.toLowerCase() !== initialName.toLowerCase(),
      )
    ) {
      newErrors.name = `A command named "${name}" already exists`;
    }

    const descError = validateDescription(description);
    if (descError) newErrors.description = descError;

    const promptError = validatePromptText(promptText);
    if (promptError) newErrors.promptText = promptError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({ name: name.toLowerCase(), description, promptText });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {/* Name */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="cmd-name" className="text-sm font-medium text-gray-900">
          Command Name
        </Label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-600 select-none">
            /
          </span>
          <Input
            ref={nameInputRef}
            id="cmd-name"
            value={name}
            onChange={(e) => setName(e.target.value.toLowerCase())}
            placeholder="summarize"
            maxLength={MAX_COMMAND_NAME_LENGTH}
            className="pl-6 font-mono text-sm"
          />
        </div>
        {errors.name ? (
          <span className="text-xs font-medium text-red-600">
            {errors.name}
          </span>
        ) : (
          <span className="text-xs text-gray-600">
            Lowercase letters, numbers, hyphens, underscores
          </span>
        )}
      </div>

      {/* Description */}
      <div className="flex flex-col gap-1.5">
        <Label
          htmlFor="cmd-description"
          className="text-sm font-medium text-gray-900"
        >
          Description{' '}
          <span className="text-gray-600 font-normal">(optional)</span>
        </Label>
        <Input
          id="cmd-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Summarize the current discussion"
          maxLength={MAX_COMMAND_DESCRIPTION_LENGTH}
          className="text-sm"
        />
        {errors.description && (
          <span className="text-xs font-medium text-red-600">
            {errors.description}
          </span>
        )}
      </div>

      {/* Prompt Text */}
      <div className="flex flex-col gap-1.5">
        <Label
          htmlFor="cmd-prompt"
          className="text-sm font-medium text-gray-900"
        >
          Prompt Text
        </Label>
        <Textarea
          id="cmd-prompt"
          value={promptText}
          onChange={(e) => setPromptText(e.target.value)}
          placeholder="Please provide a concise summary of the above conversation, highlighting key decisions and action items."
          maxLength={MAX_COMMAND_PROMPT_LENGTH}
          rows={4}
          className="text-sm resize-none"
        />
        <div className="flex items-center justify-between">
          {errors.promptText ? (
            <span className="text-xs font-medium text-red-600">
              {errors.promptText}
            </span>
          ) : (
            <span className="text-xs text-gray-600">
              This text will be inserted when you use the command
            </span>
          )}
          <span className="text-xs text-gray-600">
            {promptText.length}/{MAX_COMMAND_PROMPT_LENGTH}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 rounded transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-3 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded transition-colors"
        >
          {submitLabel}
        </button>
      </div>
    </form>
  );
};
